import { computed, nextTick, ref, toValue, type MaybeRefOrGetter, type Ref } from 'vue';

import type {
  Chat,
  GetMessagesResponse,
  Message,
  MessageDeletePayload,
  MessageReactionSummary,
  MessageReadPayload,
} from '@/shared/api';
import { translate } from '@/shared/i18n';
import { getApiErrorMessage, ProjectSettings } from '@/shared/lib';

import { useMessageContext, useMessagesList } from '../../api';
import {
  MessageDeliveryStatus,
  MessageWindowBoundary,
  type MessageInfoSchemaType,
} from '../../model';
import { useMessagesListsCache, MessagesFormatters, MessagesParsers } from '../list';

const useMessagesListController = (currentChatId: MaybeRefOrGetter<Chat['id']>) => {
  const { getContext } = useMessageContext();
  const { getLatestMessages, listMessages } = useMessagesList();

  const cachedMessages = useMessagesListsCache<Chat>(ProjectSettings.chat.messages.cache.maxChats);

  const messages = computed<MessageInfoSchemaType[]>(() =>
    MessagesFormatters.enrichBorderStyles(
      MessagesFormatters.sort(cachedMessages.getFromCache(toValue(currentChatId))),
    ),
  );
  const isShowMessages = computed<boolean>(() => messages.value.length !== 0);

  const currentState = computed<ReturnType<typeof cachedMessages.getState>>(() =>
    cachedMessages.getState(toValue(currentChatId)),
  );

  const isInitialLoading = computed<boolean>(() => currentState.value?.isLoadingInitial ?? false);
  const isLoadingOlder = computed<boolean>(() => currentState.value?.isLoadingOlder ?? false);
  const isLoadingNewer = computed<boolean>(() => currentState.value?.isLoadingNewer ?? false);
  const isRefreshingLatest = computed<boolean>(
    () => currentState.value?.isRefreshingLatest ?? false,
  );
  const hasMore = computed<boolean>(() => currentState.value?.hasMore ?? true);
  const hasNewer = computed<boolean>(() => currentState.value?.hasNewer ?? false);
  const loadError = computed<string | null>(() => currentState.value?.error ?? null);
  const navigationTargetId: Ref<Message['id'] | null> = ref<Message['id'] | null>(null);
  let latestNavigationRequestId: number = 0;
  let historyGeneration: number = 0;

  const setPaginationFromResponse = (
    chatId: Chat['id'],
    data: GetMessagesResponse | null | undefined,
  ): void => {
    const hasMore: boolean = data?.hasMore ?? false;
    const nextCursor: string | null = data?.nextCursor ?? null;

    cachedMessages.setPagination(chatId, { hasMore: hasMore && !!nextCursor, nextCursor });

    if (hasMore && !nextCursor) {
      cachedMessages.setError(chatId, translate('chat.messagesCursorMissing'));
    }
  };

  const setNewerPaginationFromResponse = (
    chatId: Chat['id'],
    data: GetMessagesResponse | null | undefined,
  ): void => {
    const hasNewer: boolean = data?.hasNewer ?? false;
    const newerCursor: string | null = data?.newerCursor ?? null;

    cachedMessages.setNewerPagination(chatId, { hasNewer: hasNewer && !!newerCursor, newerCursor });

    if (hasNewer && !newerCursor) {
      cachedMessages.setError(chatId, translate('chat.messagesCursorMissing'));
    }
  };

  const loadInitial = async (): Promise<void> => {
    const chatId = toValue(currentChatId);
    const state = cachedMessages.createStateIfNotExists(chatId);

    if (state.isInitialLoaded) {
      await refreshLatest();
      return;
    }
    if (state.isLoadingInitial) {
      return;
    }

    cachedMessages.setLoading(chatId, { isLoadingInitial: true, isLoadingOlder: false });
    cachedMessages.setError(chatId, null);

    const { isSuccess, data, error } = await listMessages({
      chatId,
      limit: ProjectSettings.chat.messages.pageSize,
    });

    if (isSuccess) {
      cachedMessages.setToCache(chatId, (data?.messages ?? []).map(MessagesParsers.message));
      setPaginationFromResponse(chatId, data);
      cachedMessages.setInitialLoaded(chatId, true);
    } else {
      cachedMessages.setError(
        chatId,
        error instanceof Error ? error.message : translate('chat.messagesLoadFailed'),
      );
    }

    cachedMessages.setLoading(chatId, { isLoadingInitial: false, isLoadingOlder: false });
  };

  const loadOlder = async (): Promise<void> => {
    const chatId = toValue(currentChatId);
    const state = cachedMessages.getState(chatId);

    if (
      !state ||
      !state.isInitialLoaded ||
      !state.hasMore ||
      !state.nextCursor ||
      state.isLoadingOlder
    ) {
      return;
    }

    const cursor: string = state.nextCursor;
    const requestGeneration: number = historyGeneration;
    cachedMessages.setLoading(chatId, { isLoadingInitial: false, isLoadingOlder: true });
    cachedMessages.setError(chatId, null);

    const { isSuccess, data, error } = await listMessages({
      chatId,
      before: cursor,
      limit: ProjectSettings.chat.messages.pageSize,
    });

    if (isSuccess && requestGeneration === historyGeneration && toValue(currentChatId) === chatId) {
      cachedMessages.setToCache(chatId, (data?.messages ?? []).map(MessagesParsers.message));
      setPaginationFromResponse(chatId, data);
    } else {
      cachedMessages.setError(
        chatId,
        error instanceof Error ? error.message : translate('chat.messagesLoadFailed'),
      );
    }

    cachedMessages.setLoading(chatId, { isLoadingInitial: false, isLoadingOlder: false });
  };

  const loadNewer = async (): Promise<void> => {
    const chatId = toValue(currentChatId);
    const state = cachedMessages.getState(chatId);

    if (
      !state ||
      !state.isInitialLoaded ||
      !state.hasNewer ||
      !state.newerCursor ||
      state.isLoadingNewer
    ) {
      return;
    }

    cachedMessages.setLoading(chatId, { isLoadingNewer: true });
    cachedMessages.setError(chatId, null);

    const { isSuccess, data, error } = await listMessages({
      after: state.newerCursor,
      chatId,
      limit: ProjectSettings.chat.messages.pageSize,
    });

    if (isSuccess) {
      cachedMessages.setToCache(chatId, (data?.messages ?? []).map(MessagesParsers.message));
      setNewerPaginationFromResponse(chatId, data);
    } else {
      cachedMessages.setError(
        chatId,
        error instanceof Error ? error.message : translate('chat.messagesLoadFailed'),
      );
    }

    cachedMessages.setLoading(chatId, { isLoadingNewer: false });
  };

  const openMessage = async (messageId: Message['id']): Promise<void> => {
    const navigationRequestId: number = ++latestNavigationRequestId;
    historyGeneration += 1;
    const chatId = toValue(currentChatId);
    const state = cachedMessages.getState(chatId);

    if (state?.messageWindow.hasMessage(messageId)) {
      if (navigationTargetId.value === messageId) {
        navigationTargetId.value = null;
        await nextTick();
      }

      navigationTargetId.value = messageId;
      return;
    }
    cachedMessages.setLoading(chatId, { isLoadingInitial: true });
    cachedMessages.setError(chatId, null);

    const { isSuccess, data, error } = await getContext({ chatId, messageId });
    if (toValue(currentChatId) !== chatId || navigationRequestId !== latestNavigationRequestId) {
      return;
    }

    if (isSuccess && data) {
      cachedMessages.replaceServerWindow(
        chatId,
        [...(data.before ?? []), data.target, ...(data.after ?? [])].map(MessagesParsers.message),
        {
          [MessageWindowBoundary.NEWER]: {
            cursor: data.newerCursor ?? null,
            hasMore: (data.hasNewer ?? false) && !!data.newerCursor,
          },
          [MessageWindowBoundary.OLDER]: {
            cursor: data.olderCursor ?? null,
            hasMore: (data.hasOlder ?? false) && !!data.olderCursor,
          },
        },
      );
      cachedMessages.setInitialLoaded(chatId, true);
      navigationTargetId.value = messageId;
    } else {
      cachedMessages.setError(chatId, getApiErrorMessage(error));
    }

    cachedMessages.setLoading(chatId, { isLoadingInitial: false });
  };

  const applyLatestPage = (
    chatId: Chat['id'],
    data: GetMessagesResponse | null | undefined,
    replaceWindow: boolean,
  ): void => {
    const latestMessages: MessageInfoSchemaType[] = (data?.messages ?? []).map(
      MessagesParsers.message,
    );

    if (!replaceWindow) {
      cachedMessages.setToCache(chatId, latestMessages);
      setPaginationFromResponse(chatId, data);
      return;
    }

    const optimisticMessages: MessageInfoSchemaType[] = cachedMessages
      .getFromCache(chatId)
      .filter(
        ({ deliveryStatus }: MessageInfoSchemaType): boolean =>
          deliveryStatus !== MessageDeliveryStatus.SENT,
      )
      .filter(
        ({ clientMessageId }: MessageInfoSchemaType): boolean =>
          !latestMessages.some((message) => message.clientMessageId === clientMessageId),
      );

    cachedMessages.replaceServerWindow(chatId, [...latestMessages, ...optimisticMessages], {
      [MessageWindowBoundary.NEWER]: { cursor: null, hasMore: false },
      [MessageWindowBoundary.OLDER]: {
        cursor: data?.nextCursor ?? null,
        hasMore: (data?.hasMore ?? false) && !!data?.nextCursor,
      },
    });
  };

  const refreshLatest = async (): Promise<void> => {
    const chatId = toValue(currentChatId);
    const state = cachedMessages.getState(chatId);

    if (!state || !state.isInitialLoaded || state.isRefreshingLatest) {
      return;
    }

    cachedMessages.setRefreshingLatest(chatId, true);

    const { isSuccess, data } = await getLatestMessages({
      chatId,
      limit: ProjectSettings.chat.messages.pageSize,
    });

    if (isSuccess) {
      applyLatestPage(chatId, data, false);
    }

    cachedMessages.setRefreshingLatest(chatId, false);
  };

  const goToLatest = async (): Promise<void> => {
    const chatId = toValue(currentChatId);
    const state = cachedMessages.getState(chatId);

    if (!state || !state.isInitialLoaded || state.isRefreshingLatest) {
      return;
    }

    const navigationRequestId: number = ++latestNavigationRequestId;
    historyGeneration += 1;
    const requestGeneration: number = historyGeneration;
    cachedMessages.setRefreshingLatest(chatId, true);

    const { isSuccess, data } = await getLatestMessages({
      chatId,
      limit: ProjectSettings.chat.messages.pageSize,
    });

    if (isSuccess && requestGeneration === historyGeneration && toValue(currentChatId) === chatId) {
      applyLatestPage(chatId, data, true);
    }

    cachedMessages.setRefreshingLatest(chatId, false);

    if (
      toValue(currentChatId) !== chatId ||
      navigationRequestId !== latestNavigationRequestId ||
      requestGeneration !== historyGeneration
    ) {
      return;
    }

    if (isSuccess) {
      navigationTargetId.value = null;
    }
  };

  const retryLoad = async (): Promise<void> => {
    if (currentState.value?.isInitialLoaded) {
      await loadOlder();
      return;
    }

    await loadInitial();
  };

  const setNewMessage = (
    chatId: Chat['id'],
    newMessage: Message & { deliveryStatus?: MessageInfoSchemaType['deliveryStatus'] },
  ): void => {
    const optimisticMessage = cachedMessages
      .getFromCache(chatId)
      .find(
        (cachedMessage) =>
          newMessage.clientMessageId &&
          cachedMessage.clientMessageId === newMessage.clientMessageId,
      );
    if (optimisticMessage && optimisticMessage.id !== newMessage.id) {
      cachedMessages.replaceInCache(
        chatId,
        optimisticMessage.id,
        MessagesParsers.message(newMessage),
      );
      return;
    }

    cachedMessages.setToCache(chatId, MessagesParsers.message(newMessage));
  };

  const replaceOptimisticMessage = (chatId: Chat['id'], message: Message): void => {
    const optimisticMessage = cachedMessages
      .getFromCache(chatId)
      .find((cachedMessage) => cachedMessage.clientMessageId === message.clientMessageId);
    if (!optimisticMessage) {
      setNewMessage(chatId, message);
      return;
    }

    cachedMessages.replaceInCache(chatId, optimisticMessage.id, MessagesParsers.message(message));
  };

  const setUpdatedMessage = (chatId: Chat['id'], updatedMessage: Message) => {
    cachedMessages.updateToCache(chatId, MessagesParsers.message(updatedMessage));
  };

  const setMessageReactions = (
    chatId: Chat['id'],
    messageId: Message['id'],
    reactions: MessageReactionSummary[] | null,
  ): void => {
    const message = cachedMessages
      .getFromCache(chatId)
      .find((cachedMessage) => cachedMessage.id === messageId);

    if (!message) {
      return;
    }

    cachedMessages.updateToCache(chatId, {
      ...message,
      reactions: (reactions ?? []).map((reaction) => ({
        ...reaction,
        reactedByCurrentUser: reaction.reactedByCurrentUser ?? false,
      })),
    });
  };

  const setOutgoingMessageStatus = (
    chatId: Chat['id'],
    clientMessageId: string,
    deliveryStatus: MessageInfoSchemaType['deliveryStatus'],
  ): void => {
    const message = cachedMessages
      .getFromCache(chatId)
      .find((cachedMessage) => cachedMessage.clientMessageId === clientMessageId);
    if (message) {
      cachedMessages.updateToCache(chatId, { ...message, deliveryStatus });
    }
  };

  const removeOptimisticMessage = (chatId: Chat['id'], clientMessageId: string): void => {
    const message = cachedMessages
      .getFromCache(chatId)
      .find((cachedMessage) => cachedMessage.clientMessageId === clientMessageId);
    if (message) {
      cachedMessages.removeFromCache(chatId, message.id);
    }
  };

  const setMessagesRead = (chatId: Chat['id'], data: MessageReadPayload) => {
    if (cachedMessages.getState(chatId)) {
      const preparedCached = MessagesFormatters.sort(cachedMessages.getFromCache(chatId));

      const readMessageIndex = preparedCached.findIndex(({ id }) => id === data.messageId);
      if (readMessageIndex !== -1) {
        const newReadAt = MessagesParsers.readAt(data.readAt);

        if (newReadAt) {
          const updatedMessage = preparedCached
            .filter((el, index) => index <= readMessageIndex && !el.isWasRead)
            .map((el) => ({ ...el, isWasRead: true, readAt: newReadAt }));

          cachedMessages.updateToCache(chatId, updatedMessage);
        }
      }
    }
  };

  const removeMessage = (chatId: Chat['id'], data: MessageDeletePayload) => {
    if (cachedMessages.getState(chatId) && data.messageId) {
      const cached = cachedMessages.getFromCache(chatId);

      const deletedMessage = cached.find(({ id }) => id === data.messageId);
      if (deletedMessage) {
        if (deletedMessage.byCurrentUser || data.forEveryone) {
          cachedMessages.updateToCache(chatId, { ...deletedMessage, deletedAt: new Date() });
        }
      }
    }
  };

  return {
    messages,
    isShowMessages,
    loadInitial,
    loadNewer,
    loadOlder,
    isInitialLoading,
    isLoadingOlder,
    isLoadingNewer,
    isRefreshingLatest,
    hasMore,
    hasNewer,
    loadError,
    navigationTargetId,
    openMessage,
    goToLatest,
    retryLoad,
    refreshLatest,
    setNewMessage,
    replaceOptimisticMessage,
    setOutgoingMessageStatus,
    removeOptimisticMessage,
    setUpdatedMessage,
    setMessageReactions,
    setMessagesRead,
    removeMessage,
  };
};

export { useMessagesListController };
